import MapPlugin from "../MapPlugin";
import axios from "axios";
import { Checkbox, FormControlLabel, FormGroup, Stack } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useOperationsMap } from "../../OperationsMapProvider";
import { useTranslation } from "react-i18next";
import LayerSettings from "../../common/LayerSettings";
import Route from "../../../../../common/Route";

export default class TrafficWarnings extends MapPlugin {

  constructor(map) {
    super("trafficwarnings", map);

    const p = this.theme.palette;

    this.defaultSettings = {
      density: false,
      warnings: false,
      cameras: false,
    };

    this.styles = {
      divider: `border: none; height: 1px; background: ${p.divider}; margin-bottom: 12px;`
    };


    this._symbols = {
      "RoadClosed": { name: "close", color: p.error.light },
      "Warning": { name: "priority-high", color: p.warning.main },
      "RoadWork": { name: "construction", color: p.warning.light },
      "RoadClosedForWinter": { name: "severe-cold", color: p.info.dark },
      "RiskOfRoadClosed": { name: "weather-mix", color: p.info.main },
      "GeneralWarning": { name: "priority-high", color: p.info.light },
      "ConvoyDriving": { name: "priority-high", color: p.warning.light },
    };

    this.addImage("priority-high");
    this.addImage("severe-cold");
    this.addImage("construction");
    this.addImage("close");
    this.addImage("weather-mix");
    this.addImage("videocam");

    // Traffic warnings popup

    this.addPopup(
      "traffic_warnings",
      "traffic_warnings_background",
      (_, properties) => `<span>${properties.message.replaceAll("|", " ")}</span>`,
      { showInDialog: this.mobile, title: this.t("operationsMap:layers:traffic:warning")  }
    );

    this.addPopup(
      "traffic_warnings_clusters",
      "traffic_warnings_cluster_circles",
      (e, _, callback) => {

        const clusterId = e.features[0].properties.cluster_id;
        const point_count = e.features[0].properties.point_count;
        const clusterSource = this._map.getSource("traffic_warnings_source");

        clusterSource.getClusterLeaves(clusterId, point_count, 0, (err, aFeatures) => {
          if (err) return;

          callback(
            aFeatures
              .slice(0, 25)
              .map(f => f.properties)
              .filter(p => !!p.message)
              .map(p => `<span>${p.message.replaceAll("|", " ")}</span>`)
              .join(`<hr style="${this.styles.divider}"/>`)
          );
        })
      },
      { contentInCallback: true, showInDialog: this.mobile, title: this.t("operationsMap:layers:traffic:warnings")  }
    );

    // Webcams popup

    this.addPopup(
      "webcams",
      "webcams_background",
      (_, properties) => TrafficWarnings.RenderCameraPopup(properties),
      { showInDialog: true, showInTooltip: !this.mobile, title: this.t("operationsMap:layers:traffic:cameras")  }
    );

    this.addPopup(
      "webcams_clusters",
      "webcams_cluster_circles",
      (e, _, callback) => {

        const clusterId = e.features[0].properties.cluster_id;
        const point_count = e.features[0].properties.point_count;
        const clusterSource = this._map.getSource("webcams");

        clusterSource.getClusterLeaves(clusterId, point_count, 0, (err, aFeatures) => {
          if (err) return;

          callback(
            aFeatures
              .slice(0, 25)
              .map(f => f.properties)
              .map(properties => TrafficWarnings.RenderCameraPopup(properties))
              .join("<br/><br/>")
          );
        })
      },
      { contentInCallback: true, showInDialog: true, showInTooltip: !this.mobile, title: this.t("operationsMap:layers:traffic:cameras")  }
    );
  }

  static RenderCameraPopup(properties) {
    return (`
    <img width="100%" height="auto" src="${TrafficWarnings.GetCameraImageUrlById(properties.id)}"></img><br/>
    <strong>${properties.info} - ${properties.name}</strong>
  `);
  }

  async updateSources() {
    const p = this.theme.palette;

    if (this.settings.warnings) {
      try {
        const res = await axios(
          "https://traffic-info.atlas.vegvesen.no/traffic-information/messages?sort=priorityScore",
          {
            headers: { "x-system-id": "assist" }
          }
        );

        if (this.destroyed) return;

        this._map.getSource("traffic_warnings_source")?.setData(Route.GeoJson(
          res.data.features
            .filter(feature => feature.properties.isActiveNow)
            .map(feature => ({
              type: "Feature",
              geometry: feature.geometry,
              properties: {
                iconColor: this._symbols[feature.properties.icon.mapMarkerIcon]?.color || p.warning.light,
                iconName: this._symbols[feature.properties.icon.mapMarkerIcon]?.name || "priority-high",
                message: feature.properties.descriptionOfTrafficMessage,
                startTime: feature.properties.startTime,
                estimatedEndTime: feature.properties.estimatedEndTime,
                updatedTime: feature.properties.updatedTime,
                trafficImpact: feature.properties.trafficImpact
              }
            }))
        ));

      } catch (err) {
        console.log(err);
      }
    }


    if (this.settings.cameras) {
      try {

        const res = await axios(
          "/datasets/webcams/no.json"
        );

        if (this.destroyed) return;

        this._map.getSource("webcams")?.setData(Route.GeoJson(
          res.data
            .map(feature => ({
              type: "Feature",
              geometry: {
                type: "point",
                coordinates: feature.coordinates
              },
              properties: {
                id: feature.id,
                name: feature.name,
                info: feature.info,
              }
            }))
        ));

      } catch (err) {
        console.log(err);
      }
    }
  }

  static GetCameraImageUrlById(id) {
    return `https://kamera.atlas.vegvesen.no/api/images/${id}`;
  }

  async onMount() {
    try {
      const p = this.theme.palette;

      this._map.addSource("traffic_warnings_source", {
        type: "geojson",
        cluster: true,
        clusterRadius: 25,
        data: {
          type: "FeatureCollection",
          features: Route.EmptyGeoJson()
        }
      });

      this._map.addSource("webcams", {
        type: "geojson",
        cluster: true,
        clusterRadius: 25,
        data: {
          type: "FeatureCollection",
          features: Route.EmptyGeoJson()
        }
      });

      // Density

      this._map.addLayer({
        "id": "traffic_density",
        "slot": "middle",
        "type": "line",
        "source": {
          "type": "vector",
          "url": "mapbox://mapbox.mapbox-traffic-v1"
        },
        "source-layer": "traffic",
        "layout": {
          "line-join": "round",
          "line-cap": "round"
        },
        "paint": {
          "line-color": [
            "case",
            ["==", ["get", "congestion"], "low"], "#9bc55d",
            ["==", ["get", "congestion"], "moderate"], "#ffcc01",
            ["==", ["get", "congestion"], "heavy"], "#ff6517",
            ["==", ["get", "congestion"], "severe"], "#d41c36",
            "#00ff00" // Fallback color if none of the above conditions are met
          ],
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            5, 0,
            18, 6
          ]
        }
      });


      // Warnings

      this._map.addLayer({
        id: "traffic_warnings_background",
        source: "traffic_warnings_source",
        slot: "elevation-2",
        type: "circle",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-radius": 16, // Adjust the radius as needed
          "circle-color": ["get", "iconColor"], // Set the desired color
          "circle-stroke-width": 2,
          "circle-stroke-color": "#FFF",
        }
      });

      this._map.addLayer({
        id: "traffic_warnings",
        source: "traffic_warnings_source",
        slot: "elevation-2",
        type: "symbol",
        filter: ["!", ["has", "point_count"]],
        "layout": {
          "icon-image": ["get", "iconName"], // Assume you have an icon named "road-works" added to the style
          "icon-size": 1.6,
          "icon-ignore-placement": true,
        },
        "paint": {
          "icon-color": "#FFF"
        }
      });

      // Add cluster layers
      this._map.addLayer({
        id: "traffic_warnings_cluster_circles",
        source: "traffic_warnings_source",
        slot: "elevation-2",
        type: "circle",
        filter: ["has", "point_count"],
        paint: {
          "circle-stroke-width": 2,
          "circle-stroke-color": "#FFF",
          "circle-color": [
            "step",
            ["get", "point_count"],
            p.warning.light,
            4,
            p.warning.main,
            9,
            p.error.light,
          ],
          "circle-radius": [
            "step",
            ["get", "point_count"],
            18,
            100,
            30,
            750,
            40
          ]
        }
      });


      this._map.addLayer({
        id: "traffic_warnings_cluster_text",
        source: "traffic_warnings_source",
        slot: "elevation-2",
        type: "symbol",
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 10
        },
      });


      // Webcams

      this._map.addLayer({
        id: "webcams_background",
        source: "webcams",
        slot: "elevation-2",
        type: "circle",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-radius": 14, // Adjust the radius as needed
          "circle-color": p.info.light, // Set the desired color
          "circle-stroke-width": 2,
          "circle-stroke-color": "#FFF",
        }
      });

      this._map.addLayer({
        id: "webcams",
        source: "webcams",
        slot: "elevation-2",
        type: "symbol",
        filter: ["!", ["has", "point_count"]],
        "layout": {
          "icon-image": "videocam", // Assume you have an icon named "road-works" added to the style
          "icon-size": 1.4,
          "icon-ignore-placement": true,
        },
        "paint": {
          "icon-color": "#FFF"
        }
      });

      // Add cluster layers
      this._map.addLayer({
        id: "webcams_cluster_circles",
        source: "webcams",
        slot: "elevation-2",
        type: "circle",
        filter: ["has", "point_count"],
        paint: {
          "circle-stroke-width": 2,
          "circle-stroke-color": "#FFF",
          "circle-color": p.info.light,
          "circle-radius": 15
        }
      });


      this._map.addLayer({
        id: "webcams_cluster_text",
        source: "webcams",
        slot: "elevation-2",
        type: "symbol",
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 14,
        },
        paint: {
          "text-color": "#FFF"
        }
      });



      this.onUpdateSettings(null, { warnings: this.settings.warnings, cameras: this.settings.cameras });

    } catch (err) {
      console.log(err);
    }
  }

  onUnMount() {
    this._map.removeLayer("traffic_density");

    this._map.removeLayer("traffic_warnings");
    this._map.removeLayer("traffic_warnings_background");
    this._map.removeLayer("traffic_warnings_cluster_text");
    this._map.removeLayer("traffic_warnings_cluster_circles");

    this._map.removeLayer("webcams");
    this._map.removeLayer("webcams_background");
    this._map.removeLayer("webcams_cluster_text");
    this._map.removeLayer("webcams_cluster_circles");


    this._map.removeSource("traffic_warnings_source");
    this._map.removeSource("traffic_density");



    this._map.removeSource("webcams");
  }


  onUpdateSettings(_, update) {
    if (update.warnings || update.cameras) {
      this.updateSources();
    }

    this._map.setLayoutProperty("traffic_density", "visibility", !!this.settings.density ? "visible" : "none");

    this._map.setLayoutProperty("traffic_warnings", "visibility", !!this.settings.warnings ? "visible" : "none");
    this._map.setLayoutProperty("traffic_warnings_background", "visibility", !!this.settings.warnings ? "visible" : "none");
    this._map.setLayoutProperty("traffic_warnings_cluster_text", "visibility", !!this.settings.warnings ? "visible" : "none");
    this._map.setLayoutProperty("traffic_warnings_cluster_circles", "visibility", !!this.settings.warnings ? "visible" : "none");


    this._map.setLayoutProperty("webcams", "visibility", !!this.settings.cameras ? "visible" : "none");
    this._map.setLayoutProperty("webcams_background", "visibility", !!this.settings.cameras ? "visible" : "none");
    this._map.setLayoutProperty("webcams_cluster_text", "visibility", !!this.settings.cameras ? "visible" : "none");
    this._map.setLayoutProperty("webcams_cluster_circles", "visibility", !!this.settings.cameras ? "visible" : "none");
  }

}



export function TrafficLayerSettings() {
  const { t } = useTranslation();
  const { operationsMap } = useOperationsMap();

  const settings = operationsMap.getSettings("trafficwarnings");
  const [active, setActive] = useState(operationsMap.isPluginActive("trafficwarnings"));
  const [density, setDensity] = useState(settings.density);
  const [warnings, setWarnings] = useState(settings.warnings);
  const [cameras, setCameras] = useState(settings.cameras);

  useEffect(() => {
    const fn = () => {
      setDensity(settings.density);
      setWarnings(settings.warnings);
      setCameras(settings.cameras);
      setActive(operationsMap.isPluginActive("trafficwarnings"));
    };

    operationsMap.on("reset", fn);

    return () => operationsMap.off("reset", fn);
  }, [operationsMap, settings]);

  // Handle plugin active state
  const onHandleAccordion = useCallback((_, val) => {
    setActive(val);

    if (val) {
      operationsMap.activatePlugin("trafficwarnings");
    } else {
      operationsMap.deactivatePlugin("trafficwarnings");
    }
  }, [operationsMap]);

  const onHandleDensity = useCallback((ev) => {
    const val = ev.target.checked;
    setDensity(val);
    operationsMap.updateSettings("trafficwarnings", { density: val });
  }, [operationsMap]);


  const onHandleWarnings = useCallback((ev) => {
    const val = ev.target.checked;
    setWarnings(val);
    operationsMap.updateSettings("trafficwarnings", { warnings: val });
  }, [operationsMap]);

  const onHandleCameras = useCallback((ev) => {
    const val = ev.target.checked;
    setCameras(val);
    operationsMap.updateSettings("trafficwarnings", { cameras: val });
  }, [operationsMap]);

  return (
    <LayerSettings title={t("operationsMap:layers:traffic:title")} open={active} onChange={onHandleAccordion}>
      <Stack spacing={1}>

        <FormGroup>
          <FormControlLabel control={<Checkbox checked={density} onChange={onHandleDensity} />} label={t("operationsMap:layers:traffic:density")} />
          <FormControlLabel control={<Checkbox checked={warnings} onChange={onHandleWarnings} />} label={t("operationsMap:layers:traffic:warnings")} />
          <FormControlLabel control={<Checkbox checked={cameras} onChange={onHandleCameras} />} label={t("operationsMap:layers:traffic:cameras")} />
        </FormGroup>

      </Stack>
    </LayerSettings>
  );
}



